import { Instagram, Facebook, Mail } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";
import { brand, contact, isPlaceholder } from "@/config/site";
import { useLanguage } from "@/context/LanguageContext";
import { useABVariant } from "@/hooks/useABVariant";
import LanguageSwitcher from "@/components/sections/LanguageSwitcher";

function TikTokIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.04-.1Z" />
    </svg>
  );
}

export default function Footer({ onOpenLead }) {
  const { t } = useLanguage();

  // A/B test — dos copies distintos del CTA del footer para descubrir cuál
  // convierte mejor. Se muestra a los visitantes y trackea automáticamente
  // impressions/conversions.
  const { variant: leadVariant, trackConversion: trackLeadConv } = useABVariant(
    "footer_lead_cta",
    ["A", "B"]
  );
  const leadCopy =
    leadVariant === "B"
      ? { title: t.lead.title || "Get $10 off your first order.", note: t.lead.footerNote }
      : { title: t.lead.footerCta, note: t.lead.footerNote };

  const socials = [
    { key: "instagram", href: contact.instagram, Icon: Instagram, ready: !isPlaceholder(contact.instagram) },
    { key: "tiktok", href: contact.tiktok, Icon: TikTokIcon, ready: !isPlaceholder(contact.tiktok) },
    { key: "facebook", href: contact.facebook, Icon: Facebook, ready: !isPlaceholder(contact.facebook) },
  ].filter((s) => s.ready);

  const shopLinks = [
    { label: t.footer.shop, href: "#flagship" },
    { label: t.footer.bestSellers, href: "#collection" },
    { label: t.footer.sizeGuide, href: "#size-guide" },
    { label: t.footer.faq, href: "#faq" },
  ];

  return (
    <footer data-testid="footer" className="bg-white">
      <div className="mx-auto max-w-7xl px-5 py-16 lg:px-10">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-3">
          <div>
            <h3 className="font-serif-display text-xl font-semibold tracking-[0.1em] text-[#111]">{brand.name}</h3>
            <p className="mt-3 max-w-xs font-body text-sm text-[#777]">{t.footer.statement}</p>
          </div>
          <nav className="flex flex-col gap-2.5">
            {shopLinks.map((l) => (
              <button
                key={l.href}
                data-testid={`footer-link-${l.href.replace("#", "")}`}
                onClick={() => scrollToSection(l.href)}
                className="text-left font-body text-sm text-[#555] transition-colors hover:text-[#111]"
              >
                {l.label}
              </button>
            ))}
          </nav>
          <div className="flex flex-col gap-4">
            {onOpenLead && (
              <button
                data-testid="footer-lead-cta"
                data-ab-variant={leadVariant || ""}
                onClick={() => {
                  trackLeadConv("open_lead_modal");
                  onOpenLead("footer");
                }}
                className="press group inline-flex items-start gap-3 rounded-2xl border border-[#e2e2e0] bg-[#fafaf8] p-4 text-left transition-colors hover:border-[#111]"
              >
                <Mail className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#111]" strokeWidth={1.5} />
                <span>
                  <span className="block font-body text-sm font-medium text-[#111]">
                    {leadCopy.title}
                  </span>
                  <span className="mt-0.5 block font-body text-xs text-[#777]">
                    {leadCopy.note}
                  </span>
                </span>
              </button>
            )}
            {socials.length > 0 && (
              <div className="flex gap-3">
                {socials.map(({ key, href, Icon }) => (
                  <a
                    key={key}
                    data-testid={`footer-${key}`}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={key}
                    className="icon-live flex h-10 w-10 items-center justify-center rounded-full border border-[#e2e2e2] text-[#111] transition-colors hover:bg-[#111] hover:text-white"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            )}
            <LanguageSwitcher className="text-[#111]" />
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-3 border-t border-[#eee] pt-6 text-xs text-[#999] sm:flex-row sm:items-center sm:justify-between">
          <div className="flex gap-5">
            <a data-testid="footer-privacy" href="#" className="transition-colors hover:text-[#111]">{t.footer.privacy}</a>
            <a data-testid="footer-terms" href="#" className="transition-colors hover:text-[#111]">{t.footer.terms}</a>
          </div>
          <p>© {new Date().getFullYear()} {brand.name}. {t.footer.rights}</p>
        </div>
      </div>
    </footer>
  );
}
